import React, {useEffect, useState} from "react";
import {Form} from "react-bootstrap";
import {FieldArray, FormikProvider, getIn} from "formik";

// AGCText looks up errors with a flat key (formik.errors[id]), which does not
// work for nested array paths like shippingAddresses[0].address — so this
// section renders its inputs directly and resolves errors with formik's getIn.
function ShippingAddressField({formik, index, field, placeHolder, readOnly}) {
    const path = `shippingAddresses[${index}].${field}`;
    const error = getIn(formik.errors, path);
    const touched = getIn(formik.touched, path);
    const showError = touched && error;
    const row = (formik.values.shippingAddresses || [])[index] || {};

    return (
        <>
            <Form.Control
                id={path}
                name={path}
                placeholder={placeHolder}
                value={row[field] || ""}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={readOnly}
                isInvalid={!!showError}
            />
            {showError && (
                <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
            )}
        </>
    );
}

export function ShippingAddressesSection({formik, readOnly = false, defaultCollapsed = false}) {
    const shippingAddresses = formik.values.shippingAddresses || [];
    const [open, setOpen] = useState(!defaultCollapsed);

    // A collapsed section would hide row validation errors — expand on a failed submit.
    const hasErrors = !!getIn(formik.errors, "shippingAddresses");
    useEffect(() => {
        if (hasErrors && formik.submitCount > 0) {
            setOpen(true);
        }
    }, [hasErrors, formik.submitCount]);

    return (
        <FormikProvider value={formik}>
            <div className="separator my-3"/>
            <div
                className="d-flex align-items-center cursor-pointer"
                role="button"
                onClick={() => setOpen((prevOpen) => !prevOpen)}>
                <h5 className="mb-0">Shipping Addresses</h5>
                {shippingAddresses.length > 0 && (
                    <span className="badge badge-light-primary ms-2">
                        {shippingAddresses.length}
                    </span>
                )}
                <i className={`bi ms-2 ${open ? "bi-chevron-up" : "bi-chevron-down"}`}></i>
            </div>
            {open && (
                <FieldArray name="shippingAddresses">
                    {({push, remove}) => (
                        <div className="mt-3 mb-2">
                            {shippingAddresses.map((shippingAddress, index) => (
                                <div
                                    className="d-flex align-items-start gap-2 mb-2"
                                    key={shippingAddress.id || index}>
                                    <div className="flex-grow-1">
                                        <ShippingAddressField
                                            formik={formik}
                                            index={index}
                                            field="address"
                                            placeHolder="Shipping Address"
                                            readOnly={readOnly}
                                        />
                                    </div>
                                    <div style={{width: "150px"}} className="flex-shrink-0">
                                        <ShippingAddressField
                                            formik={formik}
                                            index={index}
                                            field="pinCode"
                                            placeHolder="Pin Code"
                                            readOnly={readOnly}
                                        />
                                    </div>
                                    {!readOnly && (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-light-danger btn-icon flex-shrink-0"
                                            onClick={() => remove(index)}
                                            title="Remove Shipping Address">
                                            <i className="bi bi-trash"></i>
                                        </button>
                                    )}
                                </div>
                            ))}

                            {shippingAddresses.length === 0 && (
                                <p className="text-muted mb-2 fs-7">
                                    No shipping addresses added yet.
                                </p>
                            )}

                            {!readOnly && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-light-primary"
                                    onClick={() => push({address: "", pinCode: ""})}>
                                    <i className="bi bi-plus-circle me-1"></i>
                                    Add Shipping Address
                                </button>
                            )}
                        </div>
                    )}
                </FieldArray>
            )}
        </FormikProvider>
    );
}
